// Sentry must be initialized before any other imports that use react-router
import * as Sentry from '@sentry/react'
import { createBrowserRouter, RouteObject } from 'react-router'

import { RouteHydrateGate } from 'components/bootstrap-loader/route-hydrate-gate'
import { RouteErrorBoundary } from 'components/error-boundary/RouteErrorBoundary'
import { adminRoutes } from 'features/admin/routes'
import { alertsRoutes } from 'features/alerts/routes'
import { askYogiRoutes } from 'features/ask-yogi/routes'
import { AuthContainer } from 'features/auth'
import { authRoutes, requestAccessRoutes } from 'features/auth/routes'
import { catalogRoutes } from 'features/catalog/routes'
import { chartsV2Routes } from 'features/charts-v2/routes'
import { chartsRoutes } from 'features/charts/routes'
import { connectRoutes } from 'features/connect/routes'
import { coverageRoutes } from 'features/coverage/routes'
import {
  dashboardRoutes,
  sharedDashboardRoutes,
} from 'features/dashboard/Routes'
import { feedbackRoutes } from 'features/feedback/routes'
import { keywordsRoutes } from 'features/keywords/routes'
import { NotFound } from 'features/not-found'
import { pdpRoutes } from 'features/pdp/routes'
import { productWatchRoutes } from 'features/product-watch/routes'
import { profileRoutes } from 'features/profile/routes'
import { Project } from 'features/project'
import { projectHomeRoutes } from 'features/project-home/routes'
import { projectsRoutes } from 'features/projects/Routes'
import { recommendationsRoutes } from 'features/recommendations/routes'
import { savedViewsRoutes } from 'features/saved-views/routes'
import { settingsRoutes } from 'features/settings/Routes'
import { shareRoutes } from 'features/share/routes'
import { sourceUploadRoutes } from 'features/source-upload/routes'
import { trendDetectionRoutes } from 'features/trend-detection/Routes'
import { trialRoutes } from 'features/trial/routes'
import { wikiRoutes } from 'features/wiki/routes'
import { projectPath } from 'routes/builders'
import { FeatureGuardLayout } from 'routes/FeatureGuardLayout'
import { RootLayout } from 'routes/RootLayout'
import { SignedInLayout } from 'routes/SignedInLayout'
import {
  dashboardsV21Routes,
  sharedDashboardsV21Routes,
} from 'src/features/dashboards-v2-1/routes-v-2'
import {
  dashboardsV2Routes,
  sharedDashboardsV2Routes,
} from 'src/features/dashboards-v2/routes-v-2'
import 'trackers/sentry/init'

// Public routes (outside auth)
const publicRoutes: RouteObject[] = [
  // The credential share-link page: a customer with no account stores one
  // credential. Public by design — possession of the URL token is the auth.
  ...connectRoutes,
  ...shareRoutes,
  ...sharedDashboardRoutes,
  ...sharedDashboardsV2Routes,
  ...sharedDashboardsV21Routes,
]

// Auth-protected routes
const protectedRoutes: RouteObject[] = [
  ...projectsRoutes,
  ...authRoutes,
  // Account-global admin surface — a top-level tabbed shell (`/admin`) that
  // lives above any single project/hub, so it registers here as a SIBLING of
  // the project routes, NOT under `projectPath()`. Feature flags is the first
  // tab (`/admin/feature-flags`); the shell gates on `feature_flags:read`.
  ...adminRoutes,
  {
    path: projectPath(),
    element: <Project />,
    children: [
      ...projectHomeRoutes,
      {
        element: <FeatureGuardLayout />,
        children: [
          ...askYogiRoutes,
          ...chartsRoutes,
          ...chartsV2Routes,
          ...dashboardRoutes,
          ...dashboardsV2Routes,
          ...dashboardsV21Routes,
          ...trendDetectionRoutes,
          ...sourceUploadRoutes,
          ...coverageRoutes,
          ...feedbackRoutes,
          ...keywordsRoutes,
          ...catalogRoutes,
          ...productWatchRoutes,
          ...pdpRoutes,
          ...recommendationsRoutes,
          ...alertsRoutes,
          ...savedViewsRoutes,
        ],
      },
      ...wikiRoutes,
      ...settingsRoutes,
      ...profileRoutes,
      {
        path: '*',
        element: <NotFound fullHeight="container" />,
        handle: { sentryTransactionPath: '/project/:id/*' },
      },
    ],
  },
  ...trialRoutes,
  ...requestAccessRoutes,
]

// Create the router configuration
const routes: RouteObject[] = [
  {
    element: <RootLayout />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        // Pathless fallback host for lazy routes. On a cold deep-link into a
        // route with `lazy`, react-router isn't `initialized` yet and renders
        // the DEEPEST matched `hydrateFallbackElement` in place of the page.
        // Putting it here (not on the root route) keeps RootLayout mounted
        // during that window, so the vite:preloadError handler is installed
        // and useRouteSentryTransaction can still name the pageload
        // transaction while the first chunk is in flight.
        hydrateFallbackElement: <RouteHydrateGate />,
        children: [
          ...publicRoutes,
          {
            path: '/',
            element: <AuthContainer />,
            children: [
              // SignedInLayout is a pathless layout route: it mounts the global
              // notifications bell (top-right, fixed-position) for every
              // authenticated surface (project, settings, admin, profile,
              // trial, request-access). The bell itself is gated on
              // `isAuthenticated`, so it does not render on the sign-in /
              // password-setup pages that also live under `AuthContainer`.
              {
                element: <SignedInLayout />,
                children: protectedRoutes,
              },
            ],
          },
          {
            path: '*',
            element: <NotFound fullHeight="viewport" />,
            handle: { sentryTransactionPath: '/*' },
          },
        ],
      },
    ],
  },
]

// https://docs.sentry.io/platforms/javascript/guides/react/features/react-router/v7/#usage-with-createbrowserrouter-or-creatememoryrouter-data-mode
const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouterV7(createBrowserRouter)

export const router = sentryCreateBrowserRouter(routes)
